// React and Router imports
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";

// Firebase imports
import { auth } from "../../firebase";

// Mention support
import { useUserMentionTextarea } from "../../utils/useUserMentionTextarea";
import UserMentionDropdown from "../shared/UserMentionDropdown";
import { useCommentsDock, CommentsDockHeader } from "../shared/commentsDock";

// Styles
import "../../css/Document.css"
import "../../css/Comments.css"
import "../../css/SupportTicket.css"

function Comments({ type, name, uid, userEmail, userUid }) {

        const { dropdownProps: mentionDropdownProps, handleTextareaChange: handleMentionChange, handleTextareaKeyDown: handleMentionKeyDown, mentionedUids, clearMentionedUids } =
                useUserMentionTextarea({ mode: "documentation", docType: type, docName: name });

        const [hasComments, setHasComments] = useState(true)
        const [documentComments, setDocumentComments] = useState("")
        const [displayCreateComment, setDisplayCreateComment] = useState(false)
        const [displayReplyTextArea, setDisplayReplyTextArea] = useState(false)
        const [replyStates, setReplyStates] = useState({});
        const [editStates, setEditStates] = useState({})
        const [reply, setReply] = useState("")
        const [edit, setEdit] = useState("")
        const [comment, setComment] = useState("")
        const [commentError, setCommentError] = useState("")
        const navigate = useNavigate();

        // BBD-138: docked drawer state + scroll the new-comment form into view when it opens
        const { isOpen: dockOpen, toggle: toggleDock, open: openDock } = useCommentsDock();
        const newCommentRef = useRef(null);

        useEffect(() => {
                if (displayCreateComment && newCommentRef.current) {
                        newCommentRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
                }
        }, [displayCreateComment]);

        useEffect(() => {
                getAllCommentsForDocument();
        }, [type, name, uid]);

        const getAllCommentsForDocument = async () => {
                try {
                        const user = auth.currentUser;
                        const token = await user.getIdToken();
                        const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/documentation/comments/${type}/${name}/${uid}/`, {
                                method: 'GET',
                                headers: { 
                                        "Content-Type": "application/json",
                                        "Authorization": `${token}`
                                 },
                        })

                        if (response.status === 404) { setHasComments(false) }

                        const data = await response.json()
                        const sortedComments = data.comments.sort(
                                (a, b) => new Date(b.created_at) - new Date(a.created_at)
                        );
                        setDocumentComments(sortedComments);
                } catch (error) {
                        console.error("Error in fetching document comments")
                }
        }

        const handleDeleteComment = async (commentId, parentCommentId) => {
                const data = {
                        commentId: commentId,
                        parentComment: parentCommentId
                }

                try {
                        const user = auth.currentUser;
                        const token = await user.getIdToken();
                        const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/documentation/comments/delete/${type}/${name}/${uid}/`, {
                                method: "DELETE",
                                body: JSON.stringify(data),
                                headers: { 
                                        'Content-Type': 'application/json',
                                        'requesting-user-email': userEmail,
                                        'Authorization': `${token}`
                                },
                        })

                        if (response.ok) {
                                console.log("Deleted commented successfully.")
                                navigate(0)
                        } else {
                                console.error("Was not able to delete comment.")
                        }
                } catch (error) {
                        console.error("Was not able to delete comment.")
                }
        }

        // BBD-73: the document author can block a commenter straight from the
        // thread. The block hides their existing comments rather than deleting
        // them, so the page is reloaded to reflect that.
        const handleBlockCommenter = async (commenterEmail) => {
                if (!window.confirm(
                        `Block ${commenterEmail}?\n\n` +
                        `They will no longer be able to view or comment on your documents, and their existing comments will be hidden. ` +
                        `Documents you have made public stay readable by anyone who is signed out. ` +
                        `You can undo this from Account \u2192 Blocked Emails.`
                )) return;

                try {
                        const user = auth.currentUser;
                        const token = await user.getIdToken();
                        const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/users/${userUid}/blocked-emails/create/`, {
                                method: "POST",
                                body: JSON.stringify({ email: commenterEmail }),
                                headers: {
                                        'Content-Type': 'application/json',
                                        'Authorization': `${token}`
                                },
                        })

                        if (response.ok || response.status === 409) {
                                navigate(0)
                        } else {
                                const data = await response.json().catch(() => ({}));
                                setCommentError(data.error || "Was not able to block that person.")
                        }
                } catch (error) {
                        setCommentError("Was not able to block that person.")
                }
        }

        const handleSubmitButtonClick = async () => {
                const data = {
                        author: userUid,
                        originalDocumentAuthor: uid,
                        content: comment,
                        isReply: false,
                        documentName: name,
                        documentType: type,
                        mentionedUids: mentionedUids
                }

                try {
                        const user = auth.currentUser;
                        const token = await user.getIdToken();
                        const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/documentation/comments/create/`, {
                                method: "POST",
                                body: JSON.stringify(data),
                                headers: {
                                        'Content-Type': 'application/json',
                                        'requesting-user-email': userEmail,
                                        'Authorization': `${token}`
                                },
                        })

                        if (response.ok) {
                                console.log("Successfully created comment.")
                                clearMentionedUids();
                                setDisplayCreateComment(false);
                                setComment("");
                                getAllCommentsForDocument();
                                navigate(0)
                        } else if (response.status === 403) {
                                const data = await response.json().catch(() => ({}));
                                setCommentError(data.error || "You do not have permission to comment on this document.")
                        } else {
                                setCommentError("Error in creating comment.")
                        }
                } catch (error) {
                        setCommentError("Error in creating comment.")
                }
        }

        const handleEditSubmitButtonClick = async (editCommentId) => {
                const data = {
                        author: userUid,
                        originalDocumentAuthor: uid,
                        content: edit,
                        isReply: false,
                        documentName: name,
                        documentType: type,
                        commentId: editCommentId
                }

                try {
                        const user = auth.currentUser;
                        const token = await user.getIdToken();
                        const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/documentation/comments/update/`, {
                                method: "PATCH",
                                body: JSON.stringify(data),
                                headers: { 
                                        'Content-Type': 'application/json',
                                        'requesting-user-email': userEmail,
                                        'Authorization': `${token}`
                                },
                        })

                        if (response.ok) {
                                console.log("Successfully edited comment.")
                                setDisplayCreateComment(false)
                                setComment("")
                                getAllCommentsForDocument();
                                navigate(0)
                        } else {
                                console.error("Error in editing comment.")
                        }
                } catch (error) {
                        console.error("Error in editing comment.")
                }
        }

        // Theme cleanup on unmount



        const handleReplySubmitButtonClick = async (parentCommentId) => {
                const data = {
                        author: userUid,
                        originalDocumentAuthor: uid,
                        content: reply,
                        isReply: true,
                        documentName: name,
                        documentType: type,
                        parentComment: parentCommentId,
                        mentionedUids: mentionedUids
                }

                try {
                        const user = auth.currentUser;
                        const token = await user.getIdToken();
                        const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/documentation/comments/create/`, {
                                method: "POST",
                                body: JSON.stringify(data),
                                headers: {
                                        'Content-Type': 'application/json',
                                        'requesting-user-email': userEmail,
                                        'Authorization': `${token}`
                                },
                        })

                        if (response.ok) {
                                console.log("Successfully created comment.")
                                clearMentionedUids();
                                setDisplayCreateComment(false);
                                setComment("");
                                getAllCommentsForDocument();
                                navigate(0)
                        } else if (response.status === 403) {
                                const data = await response.json().catch(() => ({}));
                                setCommentError(data.error || "You do not have permission to comment on this document.")
                        } else {
                                setCommentError("Error in creating comment.")
                        }
                } catch (error) {
                        setCommentError("Error in creating comment.")
                }
        }

        const handlePlusIconClick = () => {
                // From a collapsed drawer, "+" always expands it and shows the form.
                if (!dockOpen) {
                        openDock();
                        setDisplayCreateComment(true);
                        return;
                }
                setDisplayCreateComment((prevState) => !prevState);
        };

        const handleReplyClick = (email, commentId, authorName) => {
                setReplyStates((prevState) => ({
                        ...prevState,
                        [commentId]: !prevState[commentId]
                }));
                setReply(`Reply To: ${authorName || email} | Comment ID: ${commentId}`);
        };

        const handleEditClick = (commentContent, commentId) => {
                setEditStates((prevState) => ({
                        ...prevState,
                        [commentId]: !prevState[commentId]
                }));
                setEdit(commentContent);
        }

        return (
                <>
                <div className={`Comment__div ${dockOpen ? "Comment__div--open" : "Comment__div--collapsed"}`}>
                        <CommentsDockHeader
                                count={hasComments && documentComments.length > 0 ? documentComments.length : 0}
                                isOpen={dockOpen}
                                onToggle={toggleDock}
                                onAdd={handlePlusIconClick}
                                bodyId="Comment__body-document"
                        />

                        <div className="Comment__body" id="Comment__body-document" hidden={!dockOpen}>

                        {commentError && (
                                <div className="Comment__error" role="alert">
                                        <i className="fa-solid fa-circle-exclamation"></i>
                                        <span>{commentError}</span>
                                        <button className="Comment__action-btn" onClick={() => setCommentError("")}>Dismiss</button>
                                </div>
                        )}

                        <div className="Comment__list">
                                {hasComments && documentComments.length > 0 ? (
                                        <>
                                                {documentComments.map((comment) => (
                                                        <div key={comment.id} className={`Comment__comment-area ${comment.is_reply ? 'Comment__reply' : ''}`}>
                                                                {comment.is_reply !== true && (
                                                                        <>
                                                                                <div className="Comment__meta">
                                                                                        <div className="Comment__author-info">
                                                                                                <i className="fa-solid fa-user Comment__user-icon"></i>
                                                                                                <span className="Comment__author-name">{comment.author_name || comment.author_email || "Anonymous"}</span>
                                                                                        </div>
                                                                                        <span className="Comment__timestamp">{new Date(comment.created_at).toLocaleString()}</span>
                                                                                </div>
                                                                                <p className="Comment__content">{comment.content}</p>
                                                                                <div className="Comment__actions">
                                                                                        <button className="Comment__action-btn Comment__reply-btn" onClick={() => handleReplyClick(comment.author_email, comment.id, comment.author_name)} title="Reply">
                                                                                                <i className="fa-solid fa-reply"></i>
                                                                                                Reply
                                                                                        </button>
                                                                                        {userEmail === comment.author_email && (
                                                                                                <button className="Comment__action-btn Comment__edit-btn" onClick={() => handleEditClick(comment.content, comment.id)} title="Edit">
                                                                                                        <i className="fa-solid fa-pencil"></i>
                                                                                                        Edit
                                                                                                </button>
                                                                                        )}
                                                                                        {(userEmail === comment.author_email || userUid === uid) && (
                                                                                                <button className="Comment__action-btn Comment__delete-btn" onClick={() => handleDeleteComment(comment.id, comment.parent_comment)} title={userEmail === comment.author_email ? "Delete" : "Delete (document author)"}>
                                                                                                        <i className="fa-regular fa-trash-can"></i>
                                                                                                        Delete
                                                                                                </button>
                                                                                        )}
                                                                                        {userUid === uid && userEmail !== comment.author_email && (
                                                                                                <button className="Comment__action-btn Comment__delete-btn" onClick={() => handleBlockCommenter(comment.author_email)} title="Block this person (document author)">
                                                                                                        <i className="fa-solid fa-user-slash"></i>
                                                                                                        Block
                                                                                                </button>
                                                                                        )}
                                                                                        <span className="Comment__id">ID: {comment.id}</span>
                                                                                </div>
                                                                        </>
                                                                )}
                                                                {comment.is_reply && comment.parent_comment && (
                                                                        <div>
                                                                                <div className="Comment__reply-indicator">
                                                                                        <i className="fa-solid fa-reply"></i>
                                                                                        <span>Reply</span>
                                                                                </div>
                                                                                <div className="Comment__meta">
                                                                                        <div className="Comment__author-info">
                                                                                                <i className="fa-solid fa-user Comment__user-icon"></i>
                                                                                                <span className="Comment__author-name">{comment.author_name || comment.author_email}</span>
                                                                                        </div>
                                                                                        <span className="Comment__timestamp">{new Date(comment.created_at).toLocaleString()}</span>
                                                                                </div>
                                                                                <p className="Comment__content">{comment.content}</p>
                                                                                <div className="Comment__actions">
                                                                                        <button className="Comment__action-btn Comment__reply-btn" onClick={() => handleReplyClick(comment.author_email, comment.id, comment.author_name)} title="Reply">
                                                                                                <i className="fa-solid fa-reply"></i>
                                                                                                Reply
                                                                                        </button>
                                                                                        {userEmail === comment.author_email && (
                                                                                                <button className="Comment__action-btn Comment__edit-btn" onClick={() => handleEditClick(comment.content, comment.id)} title="Edit">
                                                                                                        <i className="fa-solid fa-pencil"></i>
                                                                                                        Edit
                                                                                                </button>
                                                                                        )}
                                                                                        {(userEmail === comment.author_email || userUid === uid) && (
                                                                                                <button className="Comment__action-btn Comment__delete-btn" onClick={() => handleDeleteComment(comment.id, comment.parent_comment)} title={userEmail === comment.author_email ? "Delete" : "Delete (document author)"}>
                                                                                                        <i className="fa-regular fa-trash-can"></i>
                                                                                                        Delete
                                                                                                </button>
                                                                                        )}
                                                                                        {userUid === uid && userEmail !== comment.author_email && (
                                                                                                <button className="Comment__action-btn Comment__delete-btn" onClick={() => handleBlockCommenter(comment.author_email)} title="Block this person (document author)">
                                                                                                        <i className="fa-solid fa-user-slash"></i>
                                                                                                        Block
                                                                                                </button>
                                                                                        )}
                                                                                        <span className="Comment__id">ID: {comment.id}</span>
                                                                                </div>
                                                                        </div>
                                                                )}
                                                                {replyStates[comment.id] && (
                                                                        <div className="Comment__form">
                                                                                <textarea
                                                                                        value={reply}
                                                                                        className="Comment__comment-text-area"
                                                                                        onChange={(e) => { setReply(e.target.value); handleMentionChange(e, setReply); }}
                                                                                        onKeyDown={handleMentionKeyDown}
                                                                                        rows={5}
                                                                                        placeholder="Write your reply... (type @ to mention someone)"
                                                                                        required
                                                                                ></textarea>
                                                                                <button onClick={() => handleReplySubmitButtonClick(comment.id)} className="Comment__submit-button">
                                                                                        <i className="fa-solid fa-paper-plane"></i>
                                                                                        Submit Reply
                                                                                </button>
                                                                        </div>
                                                                )}
                                                                {editStates[comment.id] && (
                                                                        <div className="Comment__form">
                                                                                <textarea
                                                                                        value={edit}
                                                                                        className="Comment__comment-text-area"
                                                                                        onChange={(e) => { setEdit(e.target.value); handleMentionChange(e, setEdit); }}
                                                                                        onKeyDown={handleMentionKeyDown}
                                                                                        rows={5}
                                                                                        placeholder="Edit your comment..."
                                                                                        required
                                                                                ></textarea>
                                                                                <button onClick={() => handleEditSubmitButtonClick(comment.id)} className="Comment__submit-button">
                                                                                        <i className="fa-solid fa-check"></i>
                                                                                        Save Changes
                                                                                </button>
                                                                        </div>
                                                                )}
                                                        </div>
                                                ))}  
                                                {displayCreateComment && (
                                                        <div className="Comment__form Comment__new-comment" ref={newCommentRef}>
                                                                <textarea
                                                                        value={comment}
                                                                        className="Comment__comment-text-area"
                                                                        onChange={(e) => { setComment(e.target.value); handleMentionChange(e, setComment); }}
                                                                        onKeyDown={handleMentionKeyDown}
                                                                        rows={5}
                                                                        placeholder="Share your thoughts... (type @ to mention someone)"
                                                                        required
                                                                ></textarea>
                                                                <button onClick={handleSubmitButtonClick} className="Comment__submit-button">
                                                                        <i className="fa-solid fa-paper-plane"></i>
                                                                        Post Comment
                                                                </button>
                                                        </div>
                                                )}
                                        </>
                                ) : (
                                        <>
                                                <div className="Comment__empty-state">
                                                        <i className="fa-regular fa-comments Comment__empty-icon"></i>
                                                        <p className="Comment__empty-text">No comments yet. Be the first to share your thoughts!</p>
                                                </div>
                                                {displayCreateComment && (
                                                        <div className="Comment__form Comment__new-comment" ref={newCommentRef}>
                                                                <textarea
                                                                        value={comment}
                                                                        className="Comment__comment-text-area"
                                                                        onChange={(e) => { setComment(e.target.value); handleMentionChange(e, setComment); }}
                                                                        onKeyDown={handleMentionKeyDown}
                                                                        rows={5}
                                                                        placeholder="Share your thoughts... (type @ to mention someone)"
                                                                        required
                                                                ></textarea>
                                                                <button onClick={handleSubmitButtonClick} className="Comment__submit-button">
                                                                        <i className="fa-solid fa-paper-plane"></i>
                                                                        Post Comment
                                                                </button>
                                                        </div>
                                                )}
                                        </>
                                )}
                        </div>

                        </div>
                </div>
                <UserMentionDropdown {...mentionDropdownProps} />
                </>
        )

}

export default Comments